import PropTypes from "prop-types";
import React, { useEffect } from "react";
import { preloadableRoute } from "./utils/preloadableRoute";
import ScrollToTop from "./helpers/scroll-top";
import { BrowserRouter as Router, Switch, Route } from "react-router-dom";
import { ToastProvider } from "react-toast-notifications";
import { multilanguage, loadLanguages } from "redux-multilanguage";
import { connect } from "react-redux";
import { BreadcrumbsProvider } from "react-breadcrumbs-dynamic";
import { HelmetProvider } from "react-helmet-async";
import { fetchCart } from "./redux/actions/cartActions";
import { fetchWishlist } from "./redux/actions/wishlistActions";

// home page
const HomeTelescope = preloadableRoute(() => import("./pages/home/HomeTelescope"));

// shop pages
const ModernProductList = preloadableRoute(() => import("./pages/shop/ModernProductList"));

// product pages
const ModernSingleProductPage = preloadableRoute(() => import("./pages/shop-product/ModernSingleProductPage"));

export const preloadInitialRoute = () => {
  const publicUrl = process.env.PUBLIC_URL || "";
  const rootPath = publicUrl + "/";
  const cataloguePath = window.location.pathname.slice(publicUrl.length) || "/";
  if (
    window.location.pathname === rootPath ||
    /^\/(?:page\/\d+|category\/[^/]+(?:\/page\/\d+)?)\/?$/.test(cataloguePath)
  ) return ModernProductList.preload();
  if (window.location.pathname === publicUrl + "/home") return HomeTelescope.preload();
  const productPrefix = publicUrl + "/product/";
  const productPath = window.location.pathname.startsWith(productPrefix)
    ? window.location.pathname.slice(productPrefix.length).replace(/\/$/, "")
    : "";
  if (productPath && !productPath.includes("/")) return ModernSingleProductPage.preload();
  return Promise.resolve();
};

// other pages
const About = preloadableRoute(() => import("./pages/other/About"));
const Contact = preloadableRoute(() => import("./pages/other/Contact"));
const Cart = preloadableRoute(() => import("./pages/other/Cart"));
const Wishlist = preloadableRoute(() => import("./pages/other/Wishlist"));
const Checkout = preloadableRoute(() => import("./pages/other/Checkout"));
const OrderConfirmation = preloadableRoute(() => import("./pages/other/OrderConfirmation"));
const PaymentCallback = preloadableRoute(() => import("./pages/other/PaymentCallback"));
const LoginRegister = preloadableRoute(() => import("./pages/other/LoginRegister"));
const MyAccount = preloadableRoute(() => import("./pages/other/MyAccount"));
const PrivacyPolicy = preloadableRoute(() => import("./pages/other/PrivacyPolicy"));
const TermsConditions = preloadableRoute(() => import("./pages/other/TermsConditions"));
const RefundPolicy = preloadableRoute(() => import("./pages/other/RefundPolicy"));
const NotFound = preloadableRoute(() => import("./pages/other/NotFound"));

const App = ({ dispatch }) => {
  useEffect(() => {
    dispatch(
      loadLanguages({
        languages: {
          en: require("./translations/english.json")
        }
      })
    );
    dispatch(fetchCart());
    dispatch(fetchWishlist());
  }, [dispatch]);

  return (
    <HelmetProvider>
      <ToastProvider placement="bottom-left">
        <BreadcrumbsProvider>
          <Router>
            <ScrollToTop>
              <Switch>
                <Route
                  exact
                  path={process.env.PUBLIC_URL + "/"}
                  component={ModernProductList}
                />
                <Route
                  exact
                  path={process.env.PUBLIC_URL + "/page/:page"}
                  component={ModernProductList}
                />
                <Route
                  exact
                  path={process.env.PUBLIC_URL + "/category/:category"}
                  component={ModernProductList}
                />
                <Route
                  exact
                  path={process.env.PUBLIC_URL + "/category/:category/page/:page"}
                  component={ModernProductList}
                />

                {/* Home and shop pages */}
                <Route
                  exact
                  path={process.env.PUBLIC_URL + "/home"}
                  component={HomeTelescope}
                />
                <Route
                  exact
                  path={process.env.PUBLIC_URL + "/shop"}
                  component={ModernProductList}
                />

                {/* Product pages */}
                <Route
                  exact
                  path={process.env.PUBLIC_URL + "/product/:id"}
                  component={ModernSingleProductPage}
                />

                {/* Other pages */}
                <Route
                  exact
                  path={process.env.PUBLIC_URL + "/about"}
                  component={About}
                />
                <Route
                  exact
                  path={process.env.PUBLIC_URL + "/contact"}
                  component={Contact}
                />
                <Route
                  exact
                  path={process.env.PUBLIC_URL + "/cart"}
                  component={Cart}
                />
                <Route
                  exact
                  path={process.env.PUBLIC_URL + "/wishlist"}
                  component={Wishlist}
                />
                <Route
                  exact
                  path={process.env.PUBLIC_URL + "/checkout"}
                  component={Checkout}
                />
                <Route
                  exact
                  path={process.env.PUBLIC_URL + "/order-confirmation/:orderNumber"}
                  component={OrderConfirmation}
                />
                <Route
                  exact
                  path={process.env.PUBLIC_URL + "/payment-callback/:status?"}
                  component={PaymentCallback}
                />
                <Route
                  exact
                  path={process.env.PUBLIC_URL + "/payment-failed/:orderNumber"}
                  component={PaymentCallback}
                />
                <Route
                  exact
                  path={process.env.PUBLIC_URL + "/payment-pending/:orderNumber"}
                  component={PaymentCallback}
                />
                <Route
                  exact
                  path={process.env.PUBLIC_URL + "/login-register"}
                  component={LoginRegister}
                />
                <Route
                  exact
                  path={process.env.PUBLIC_URL + "/my-account"}
                  component={MyAccount}
                />
                <Route
                  exact
                  path={process.env.PUBLIC_URL + "/privacy-policy"}
                  component={PrivacyPolicy}
                />
                <Route
                  exact
                  path={process.env.PUBLIC_URL + "/terms-conditions"}
                  component={TermsConditions}
                />
                <Route
                  exact
                  path={process.env.PUBLIC_URL + "/refund-policy"}
                  component={RefundPolicy}
                />

                <Route
                  exact
                  path={process.env.PUBLIC_URL + "/not-found"}
                  component={NotFound}
                />

                <Route exact component={NotFound} />
              </Switch>
            </ScrollToTop>
          </Router>
        </BreadcrumbsProvider>
      </ToastProvider>
    </HelmetProvider>
  );
};

App.propTypes = {
  dispatch: PropTypes.func
};

export default connect()(multilanguage(App));
